import { UserSessionRepository } from "@authentication/data/repositories/user-session.repository";
import { LanguageRepository } from "@core/data/repositories/language.repository";
import {
    Language,
    LanguageIsoCodeEnum,
} from "@core/domain/models/language.model";
import { Notification } from "@core/domain/models/notification.model";
import type { Undefinable } from "@core/domain/types/undefinable.type";
import { DismissAllNotificationsUseCase } from "@core/domain/usecases/dismiss-all-notifications.usecase";
import { DismissNotificationUseCase } from "@core/domain/usecases/dismiss-notification.usecase";
import { GetAllLanguagesUseCase } from "@core/domain/usecases/get-all-languages.usecase";
import { GetMyNotificationsUseCase } from "@core/domain/usecases/get-my-notifications.usecase";
import { GetUnreadNotificationCountUseCase } from "@core/domain/usecases/get-unread-notification-count.usecase";
import { MarkNotificationReadUseCase } from "@core/domain/usecases/mark-notification-read.usecase";
import { UpdateLanguageUseCase } from "@core/domain/usecases/update-current-language.usecase";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { GlobalEntity } from "@entity/domain/models/global-entity.model";
import { GetAllGlobalEntitiesUseCase } from "@entity/domain/usecases/get-all-global-entities.usecase";
import { ImpersonateEntityUseCase } from "@user/domain/usecases/impersonate-entity.usecase";
import { inject, injectable } from "inversify";
import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from "mobx";

@injectable()
export class IncHeaderViewModel extends BaseViewModel {
    @observable
    initialLoading: boolean = true;

    @observable
    dropdownOpen: boolean = false;

    @observable
    notificationsDropdownOpen: boolean = false;

    @observable
    notifications: Notification[] = [];

    @observable
    unreadCount: number = 0;

    @observable
    notificationsLoading: boolean = false;

    @observable
    languages: Language[] = [];

    @observable
    currentLanguage: Undefinable<LanguageIsoCodeEnum> = undefined;

    @observable
    entityId: Undefinable<number> = undefined;

    @observable
    entityName: Undefinable<string> = undefined;

    @computed
    get globalEntities(): GlobalEntity[] {
        return this.getAllGlobalEntitiesUseCase.globalEntities;
    }

    constructor(
        @inject(GetAllLanguagesUseCase)
        private readonly getAllLanguagesUseCase: GetAllLanguagesUseCase,
        @inject(GetAllGlobalEntitiesUseCase)
        private readonly getAllGlobalEntitiesUseCase: GetAllGlobalEntitiesUseCase,
        @inject(UpdateLanguageUseCase)
        private readonly updateLanguageUseCase: UpdateLanguageUseCase,
        @inject(LanguageRepository)
        private readonly languageRepository: LanguageRepository,
        @inject(UserSessionRepository)
        private readonly userSessionRepository: UserSessionRepository,
        @inject(ImpersonateEntityUseCase)
        private readonly impersonateEntityUseCase: ImpersonateEntityUseCase,
        @inject(GetMyNotificationsUseCase)
        private readonly getMyNotificationsUseCase: GetMyNotificationsUseCase,
        @inject(GetUnreadNotificationCountUseCase)
        private readonly getUnreadNotificationCountUseCase: GetUnreadNotificationCountUseCase,
        @inject(MarkNotificationReadUseCase)
        private readonly markNotificationReadUseCase: MarkNotificationReadUseCase,
        @inject(DismissNotificationUseCase)
        private readonly dismissNotificationUseCase: DismissNotificationUseCase,
        @inject(DismissAllNotificationsUseCase)
        private readonly dismissAllNotificationsUseCase: DismissAllNotificationsUseCase,
    ) {
        super();
        makeObservable(this);

        this.addSub(
            this.languageRepository.currentLanguage.subscribe(
                (currentLanguage) => {
                    this.currentLanguage = currentLanguage;
                },
            ),
        );

        this.addSub(
            this.userSessionRepository.user.subscribe((user) => {
                this.entityId = user?.entityId;
                this.entityName = user?.entityName;
            }),
        );
    }

    override async didMount(): Promise<void> {
        await super.didMount();

        this.initViewData();
    }

    async initViewData(): Promise<void> {
        Promise.all([this.getAllLanguages(), this.getGlobalAllEntities()]);

        // Best-effort unread count (non-blocking)
        this.refreshUnreadCount();

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    async getGlobalAllEntities(): Promise<void> {
        await this.getAllGlobalEntitiesUseCase.execute();
    }

    @action
    async getAllLanguages(): Promise<void> {
        const languages = await this.getAllLanguagesUseCase.execute();

        runInAction(() => {
            this.languages = languages;
        });
    }

    @action
    setEntity(entityId: Undefinable<number>): void {
        this.entityId = entityId;
    }

    @action
    setCurrentLanguage(language: LanguageIsoCodeEnum): void {
        this.currentLanguage = language;
    }

    async changeLanguage(): Promise<void> {
        if (this.currentLanguage) {
            await this.updateLanguageUseCase.execute(this.currentLanguage);
            await this.getAllLanguages();
        }
    }

    async impersonateEntity(): Promise<void> {
        await this.impersonateEntityUseCase.execute(this.entityId ?? null);
    }

    @action
    setNotificationsDropdownOpen(open: boolean): void {
        this.notificationsDropdownOpen = open;
        if (open) {
            this.refreshNotifications();
        }
    }

    @action
    async refreshUnreadCount(): Promise<void> {
        const result = await this.getUnreadNotificationCountUseCase.execute();
        if (result.isLeft()) return;

        runInAction(() => {
            this.unreadCount = result.getOrThrow();
        });
    }

    @action
    async refreshNotifications(): Promise<void> {
        this.notificationsLoading = true;
        const result = await this.getMyNotificationsUseCase.execute(20);

        runInAction(() => {
            this.notificationsLoading = false;
        });

        if (result.isLeft()) return;

        runInAction(() => {
            this.notifications = result.getOrThrow();
            this.unreadCount = this.notifications.filter(
                (n) => n.isUnread,
            ).length;
        });
    }

    async markAsRead(notificationId: number): Promise<void> {
        const result =
            await this.markNotificationReadUseCase.execute(notificationId);
        if (result.isLeft()) return;

        await this.refreshNotifications();
    }

    async dismissNotification(notificationId: number): Promise<void> {
        const result =
            await this.dismissNotificationUseCase.execute(notificationId);
        if (result.isLeft()) return;

        await Promise.all([
            this.refreshUnreadCount(),
            this.refreshNotifications(),
        ]);
    }

    async dismissAllNotifications(): Promise<void> {
        const result = await this.dismissAllNotificationsUseCase.execute();
        if (result.isLeft()) return;

        await Promise.all([
            this.refreshUnreadCount(),
            this.refreshNotifications(),
        ]);
    }

    @action
    setDropdownOpen(dropdownOpen: boolean): void {
        this.dropdownOpen = dropdownOpen;
    }
}
